/**
 * @file axios.js
 * @description Axios instance wrapper used by API modules. Provides configured axiosInstance(token) function.
 */

import axios from "axios";
import { API_BASE_URL } from "../config/env";

// Cache for axios instances based on tokens
const instanceCache = new Map();


/** * Creates or retrieves a cached axios instance with the provided token.
 * If no token is provided, a default instance without Authorization header is returned.
 * @param {string} token - Authorization token for the instance.
 * @returns {import('axios').AxiosInstance} Configured axios instance.
 */
export const axiosInstance = (token = "") => {
    // If no token is provided, return a default instance
    if (!token) {
        return axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                "Rhomtrip-Source": "rhomtrip"
            }
        });
    }

    // Check if an instance with this token already exists
    if (instanceCache.has(token)) {
        return instanceCache.get(token);
    }

    // Create a new axios instance with the token
    const instance = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
        }
    });

    // Response interceptor to handle 401 Unauthorized globally
    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response && error.response.status === 401) {
                instanceCache.delete(token);
            }
            return Promise.reject(error);
        }
    );

    // Cache the instance for future use
    instanceCache.set(token, instance);
    return instance;
}

/**
 * Clears the instance cache for a specific token or all tokens.
 * Useful for logout or token refresh scenarios.
 * @param {string} [token] - Specific token to clear, or undefined to clear all.
 */
export const clearInstanceCache = (token) => {
    if (token) {
        instanceCache.delete(token);
    } else {
        instanceCache.clear();
    }
}